import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { format } from 'd3-format';
import useMediaQuery from 'react-use-media-query-hook';

import SubChapter from './SubChapter';
import {
  sumArrays,
  diffArray,
  costCalculator as calculator,
  toNumberOrZero,
} from '../../../utils/helpers';

const makeCumulative = (arr) => {
  const result = arr.map((o, i) => {
    if (i - 1 >= 0) {
      const thisValue =
        toNumberOrZero(o) +
        arr
          .slice(0, i)
          .reduce((sum, x) => toNumberOrZero(sum) + toNumberOrZero(x));

      return thisValue < 0 ? Math.ceil(thisValue) : Math.floor(thisValue);
    }
    return o < 0 ? Math.ceil(o) : Math.floor(o);
  });

  return result;
};

const Forecast = ({
  locales: { finance, chart, chartShort },
  background,
  totalColor,
  borderCardColor,
  backgroundRow,
  secondTitleColor,
  textColor,
  borderVerticalTableColor,
  summaryMode = false,
  portfolioWebMode = false,
}) => {
  const isMobile = useMediaQuery('(max-width: 1023px)');
  const [cashIn, setCashIn] = useState([]);
  const [cashInTotal, setCashInTotal] = useState([]);
  const [fixed, setFixed] = useState([]);
  const [variable, setVariable] = useState([]);
  const [cashOutTotal, setCashOutTotal] = useState([]);
  const [monthlyCashInCashOut, setMonthlyCashInCashOut] = useState([]);
  const [cumulativeBalance, setCumulativeBalance] = useState([]);

  const { collection: products } = useSelector((state) => state.product);
  const { collection: cost } = useSelector((state) => state.cost);
  const chartNames = summaryMode ? chartShort : chart;

  const isBigNumber = (number) => {
    return Math.abs(number) > 99999;
  };

  const formatNumber = (number, showDecimals = false) => {
    if (isMobile || isBigNumber(number)) {
      // For big numbers, use SI notation with more precision
      if (isBigNumber(number)) {
        return format('.2s')(number).replace('G', 'B');
      }
      // For smaller numbers on mobile, preserve decimals
      return showDecimals ? format('.2f')(number) : number;
    }
    return showDecimals ? number.toFixed(2) : number;
  };

  useEffect(() => {
    const newCashIn = products.map((product) => {
      const endUserPrice = +product.endUserPrice || 0;
      const months = product.sales.map((s) =>
        toNumberOrZero(toNumberOrZero(+s * endUserPrice).toFixed(2)),
      );

      return {
        name: product.name,
        months,
      };
    });

    const productArrays = newCashIn.map((i) => i.months);
    const newCashInTotal = sumArrays(...productArrays);

    const newFixed = cost.map((c) => {
      // if the period is not Manual then all the items in the array are the same
      // otherwise we use the values from c but we need to fill in the missing values
      let months = [];
      switch (c.period) {
        case 'Manual':
          months = [...Array(12)].map(
            (_, i) =>
              toNumberOrZero(toNumberOrZero(c.amount?.[i]).toFixed(2)) || 0,
          );
          break;
        case 'One Off':
          // One off should only be in the first month
          months = [...Array(12)].map((_, i) => {
            if (i === 0) {
              return (
                toNumberOrZero(toNumberOrZero(c.amount?.[0] ?? 0).toFixed(2)) ||
                0
              );
            }
            return 0;
          });
          break;
        default:
          months = new Array(12).fill(
            toNumberOrZero(toNumberOrZero(calculator(c)).toFixed(2)) || 0,
          );
          break;
      }

      return {
        name: c.name,
        months,
      };
    });

    const newVariable = products.map((p) => {
      const months = p.sales.map(
        (i) => toNumberOrZero(toNumberOrZero(+p.costPrice * i).toFixed(2)) || 0,
      );
      return {
        name: p.name,
        months,
      };
    });

    const newCashOutTotal = sumArrays(
      ...[
        ...newFixed.map((i) => i.months),
        ...newVariable.map((i) => i.months),
      ],
    );

    const newMonthlyCashInCashOut = diffArray(newCashInTotal, newCashOutTotal);

    const newCumulativeBalance = makeCumulative(newMonthlyCashInCashOut);

    setCashIn(
      newCashIn.map((i) => ({
        ...i,
        months: i.months.map((j) => Math.floor(j)),
      })),
    );
    setCashInTotal(newCashInTotal.map((i) => Math.floor(i)));
    setFixed(
      newFixed.map((i) => ({
        ...i,
        months: i.months.map((j) => Math.floor(j)),
      })),
    );
    setVariable(newVariable);
    setCashOutTotal(
      newCashOutTotal.map((i) => {
        return i < 0 ? Math.ceil(i) : Math.floor(i);
      }),
    );
    setMonthlyCashInCashOut(
      newMonthlyCashInCashOut.map((i) => {
        return i < 0 ? Math.ceil(i) : Math.floor(i);
      }),
    );
    setCumulativeBalance(newCumulativeBalance);
  }, [products, cost]);

  const getBorderRightColor = () => {
    return (
      background && {
        borderRightColor: background,
      }
    );
  };

  const getBorderBottomColor = () => {
    return (
      background && {
        borderBottomColor: background,
      }
    );
  };

  const getBackgroundColor = (index) => {
    return index % 2 !== 0
      ? {
          backgroundColor: backgroundRow,
        }
      : {};
  };

  return (
    <div className="chapter forecast">
      {/* <div
        className="main-title custom-font-title"
        style={{ color: titleColor }}
      >
        {finance.forecast.title}
      </div> */}
      <div
        className={classNames({
          'forecast-inner mt-6': !summaryMode,
          'forecast-inner--web': portfolioWebMode,
        })}
        style={{ borderColor: borderCardColor, color: textColor }}
      >
        <SubChapter
          name={finance.forecast.cashIn}
          className="mb-6!"
          textColor={secondTitleColor}
        />

        <div className="table-wrapper mt-0">
          <table className="forecast custom-font-body">
            <thead>
              <tr>
                {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                <th>{finance.forecast.productName}</th>
                {chartNames.map((i, index) => (
                  <th key={index}>{i}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cashIn.map((c, index) => (
                <tr
                  key={index}
                  style={{
                    ...getBorderBottomColor(),
                    ...getBackgroundColor(index),
                  }}
                >
                  <td
                    className="left-align"
                    style={{
                      ...getBorderRightColor(),
                      borderRightColor: borderVerticalTableColor,
                    }}
                  >
                    {c.name}
                  </td>
                  {c.months.map((p, i) => (
                    <td
                      key={i}
                      style={
                        i + 1 !== c.months.length
                          ? {
                              ...getBorderRightColor(),
                              borderRightColor: borderVerticalTableColor,
                            }
                          : {}
                      }
                    >
                      {formatNumber(p)}
                    </td>
                  ))}
                </tr>
              ))}
              <tr className="total">
                <td
                  className="left-align"
                  style={{
                    ...getBorderRightColor(),
                    borderRightColor: borderVerticalTableColor,
                    borderTopColor: totalColor,
                  }}
                >
                  {finance.forecast.totalCashIn}
                </td>
                {cashInTotal.map((i, index) => (
                  <td
                    key={index}
                    style={{
                      borderTopColor: totalColor,
                      borderRightColor:
                        i + 1 !== cashInTotal.length
                          ? background || borderVerticalTableColor
                          : 'none',
                    }}
                  >
                    {formatNumber(i)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        <SubChapter
          name={finance.forecast.cashOut}
          className="mt-8 mb-6!"
          textColor={secondTitleColor}
        />

        <div className="table-wrapper mt-0">
          <table className="forecast custom-font-body">
            <thead>
              <tr>
                <th className="left-align">{finance.forecast.variableCost}</th>
                {chartNames.map((i, index) => (
                  <th key={index}>{i}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {variable.map((v, index) => (
                <tr
                  key={index}
                  style={{
                    ...getBorderBottomColor(),
                    ...getBackgroundColor(index),
                  }}
                >
                  <td
                    className="left-align"
                    style={{
                      ...getBorderRightColor(),
                      borderRightColor: borderVerticalTableColor,
                    }}
                  >
                    {v.name}
                  </td>
                  {v.months.map((m, i) => (
                    <td
                      key={i}
                      style={
                        i !== v.months.length - 1
                          ? {
                              ...getBorderRightColor(),
                              borderRightColor: borderVerticalTableColor,
                            }
                          : {}
                      }
                    >
                      {formatNumber(m, true)}
                    </td>
                  ))}
                </tr>
              ))}

              <tr className="variable-cost" style={getBorderBottomColor()}>
                <th className="left-align">{finance.forecast.fixedCost}</th>
              </tr>
              {fixed.map((fixedCost, fixedCostIndex) => (
                <tr
                  key={fixedCostIndex}
                  style={{
                    ...getBorderBottomColor(),
                    ...getBackgroundColor(fixedCostIndex),
                  }}
                >
                  <td
                    className="left-align"
                    style={{
                      ...getBorderRightColor(),
                      borderRightColor: borderVerticalTableColor,
                    }}
                  >
                    {fixedCost.name}
                  </td>
                  {fixedCost.months.map((m, i) => (
                    <td
                      key={i}
                      style={
                        i + 1 !== fixedCost.months.length
                          ? {
                              ...getBorderRightColor(),
                              borderRightColor: borderVerticalTableColor,
                            }
                          : {}
                      }
                    >
                      {formatNumber(m)}
                    </td>
                  ))}
                </tr>
              ))}

              <tr className="total">
                <td
                  className="left-align"
                  style={{
                    ...getBorderRightColor(),
                    borderRightColor: borderVerticalTableColor,
                    borderTopColor: totalColor,
                  }}
                >
                  {finance.forecast.totalCashOut}
                </td>
                {cashOutTotal.map((o, i) => (
                  <td
                    key={i}
                    style={{
                      borderTopColor: totalColor,
                      borderRightColor:
                        i + 1 !== cashOutTotal.length
                          ? background || borderVerticalTableColor
                          : 'none',
                    }}
                  >
                    {formatNumber(o)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        <SubChapter
          name={finance.forecast.balance}
          className="mt-8 mb-6!"
          textColor={secondTitleColor}
        />

        <div className="table-wrapper mt-0">
          <table className="forecast custom-font-body">
            <thead>
              <tr>
                {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                <th />
                {chartNames.map((i, index) => (
                  <th key={index}>{i}</th>
                ))}
              </tr>
            </thead>
            <tbody className="balance">
              <tr style={getBorderBottomColor()}>
                <td
                  className="left-align"
                  style={{
                    ...getBorderRightColor(),
                    borderRightColor: borderVerticalTableColor,
                  }}
                >
                  {finance.forecast.monthlyCashInOut}
                </td>
                {monthlyCashInCashOut.map((i, index) => (
                  <td
                    key={index}
                    style={
                      index + 1 !== monthlyCashInCashOut.length
                        ? {
                            ...getBorderRightColor(),
                            borderRightColor: borderVerticalTableColor,
                          }
                        : {}
                    }
                  >
                    {formatNumber(i)}
                  </td>
                ))}
              </tr>
              <tr>
                <td
                  className="left-align"
                  style={{
                    ...getBorderRightColor(),
                    borderRightColor: borderVerticalTableColor,
                  }}
                >
                  {finance.forecast.cumulativeBalance}
                </td>
                {cumulativeBalance.map((i, index) => (
                  <td
                    key={index}
                    style={
                      index + 1 !== cumulativeBalance.length
                        ? {
                            ...getBorderRightColor(),
                            borderRightColor: borderVerticalTableColor,
                          }
                        : {}
                    }
                  >
                    {formatNumber(i)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Forecast;
